// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { LazyImageProps } from "@turnerBlueprint/MobiusOverrides/LazyImage";
import * as React from "react";

interface BenefitBucketsFields {
    fields: {
        title: string;
        subtitle: string;
        subtitleLink: LinkFieldValue;
        image: string;
        imageCaption: string;
    };
}

const enum fields {
    gapBetweenCards = "gapBetweenCards",
    tabletGapBetweenCards = "tabletGapBetweenCards",
    mobileGapBetweenCards = "mobileGapBetweenCards",
    showBorder = "showBorder",
    imagePosition = "imagePosition",
    tabletImagePosition = "tabletImagePosition",
    mobileImagePosition = "mobileImagePosition",
    imageMaxHeight = "imageMaxHeight",
    cardContentGap = "cardContentGap",
    mobileCardContentGap = "mobileCardContentGap",
    contentAligment = "contentAligment",
    tabletContentAligment = "tabletContentAligment",
    mobileContentAligment = "mobileContentAligment",
    cards = "cards",
}

interface OwnProps extends WidgetProps {
    fields: {
        gapBetweenCards: number;
        tabletGapBetweenCards: number;
        mobileGapBetweenCards: number;
        showBorder: boolean;
        imageMaxHeight: number;
        imagePosition: string;
        tabletImagePosition: string;
        mobileImagePosition: string;
        cardContentGap: number;
        mobileCardContentGap: number;
        contentAligment: string;
        tabletContentAligment: string;
        mobileContentAligment: string;
        [fields.cards]: BenefitBucketsFields[];
    };
}

type Props = OwnProps;

interface BenefitBucketsStyles {
    container?: InjectableCss;
    content?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    cardContentWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    subtitle?: TypographyPresentationProps;
}

export const benefitBucketsStyles: BenefitBucketsStyles = {};

const BenefitBucketss: React.FC<Props> = ({ fields }) => {
    if (!fields.cards || fields.cards.length === 0) {
        return null;
    }

    const styles = benefitBucketsStyles;
    const history = useHistory();
    const { isInShell } = useShellContext();
    const links = useGetLinks(fields.cards, o => o.fields.subtitleLink || {});
    const onClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    return (
        <StyledWrapper
            {...styles.container}
            {...{
                numberOfCards: fields.cards.length,
                gapBetweenCards: fields.gapBetweenCards,
                tabletGapBetweenCards: fields.tabletGapBetweenCards,
                mobileGapBetweenCards: fields.mobileGapBetweenCards,
            }}
        >
            {fields.cards.map((card, index) => {
                const link = links[index];
                return (
                    <StyledWrapper
                        {...styles.content}
                        {...{
                            imagePosition: fields.imagePosition,
                            mobileImagePosition: fields.mobileImagePosition,
                            showBorder: fields.showBorder,
                            cardContentGap: fields.cardContentGap,
                            mobileCardContentGap: fields.mobileCardContentGap,
                        }}
                        key={`${card.fields.title}-${index}`}
                    >
                        <StyledWrapper
                            {...styles.imageWrapper}
                            {...{
                                numberOfCards: fields.cards.length,
                                imageHeight: fields.imageMaxHeight,
                                tabletContentAligment: fields.tabletContentAligment,
                                mobileImagePosition: fields.mobileImagePosition,
                            }}
                        >
                            <LazyImage
                                src={card.fields.image}
                                {...styles.image}
                                altText={card.fields.imageCaption || "Benefit Bucket Image"}
                            />
                        </StyledWrapper>
                        <StyledWrapper
                            {...styles.cardContentWrapper}
                            {...{
                                contentAligment: fields.contentAligment,
                                tabletContentAligment: fields.tabletContentAligment,
                                mobileContentAligment: fields.mobileContentAligment,
                            }}
                        >
                            <Typography {...styles.title}>{card.fields.title}</Typography>
                            {card.fields.subtitleLink && link?.url ? (
                                <Link href={link.url} color="text" aria-label={card.fields.subtitle}>
                                    <Typography {...styles.subtitle}>{card.fields.subtitle}</Typography>
                                </Link>
                            ) : (
                                <Typography {...styles.subtitle}>{card.fields.subtitle}</Typography>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                );
            })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BenefitBucketss,
    definition: {
        group: "XNGAGE_CUSTOM_WIDGET" as any,
        fieldDefinitions: [
            {
                name: fields.gapBetweenCards,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.tabletGapBetweenCards,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.mobileGapBetweenCards,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.showBorder,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.imagePosition,
                displayName: "Image Position",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "row" },
                    { displayName: "Right", value: "row-reverse" },
                    { displayName: "top", value: "column" },
                    { displayName: "bottom", value: "column-reverse" },
                ],
                defaultValue: "row",
                fieldType: "General",
            },
            {
                name: fields.tabletImagePosition,
                displayName: "Tablet Image Position",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Hide", value: "hide" },
                    { displayName: "Left", value: "row" },
                    { displayName: "Right", value: "row-reverse" },
                    { displayName: "top", value: "column" },
                    { displayName: "bottom", value: "column-reverse" },
                ],
                defaultValue: "hide",
                fieldType: "General",
            },
            {
                name: fields.mobileImagePosition,
                displayName: "Mobile Image Position",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Hide", value: "hide" },
                    { displayName: "Left", value: "row" },
                    { displayName: "Right", value: "row-reverse" },
                    { displayName: "top", value: "column" },
                    { displayName: "bottom", value: "column-reverse" },
                ],
                defaultValue: "hide",
                fieldType: "General",
            },
            {
                name: fields.imageMaxHeight,
                editorTemplate: "IntegerField",
                defaultValue: 91,
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.contentAligment,
                displayName: "Content Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "right", value: "right" },
                ],
                defaultValue: "center",
                fieldType: "General",
            },
            {
                name: fields.tabletContentAligment,
                displayName: "Tablet Content Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "right", value: "right" },
                ],
                defaultValue: "center",
                fieldType: "General",
            },
            {
                name: fields.mobileContentAligment,
                displayName: "Mobile Content Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "right", value: "right" },
                ],
                defaultValue: "center",
                fieldType: "General",
            },
            {
                name: fields.cardContentGap,
                editorTemplate: "IntegerField",
                defaultValue: 12,
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.mobileCardContentGap,
                editorTemplate: "IntegerField",
                defaultValue: 12,
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.cards,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title || "Turner Info Card";
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "imageCaption",
                        displayName: "Image Caption",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields.image,
                    },

                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                        maxLength: 40,
                    },
                    {
                        name: "subtitle",
                        displayName: "Card Subtitle",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                    },
                    {
                        name: "subtitleLink",
                        displayName: "Card Subtitle Url",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
